<template lang="pug">
v-navigation-drawer(
    :model-value='modelValue'
    @update:model-value='(v) => emit("update:modelValue", v)'
    location='right'
    width='420'
    temporary
    style='background: #1a1a22; color: white;'
)
    div(style='display: flex; flex-direction: column; height: 100%;')
        //- Header
        div(style='padding: 10px 14px; border-bottom: 1px solid rgba(255,255,255,0.08); display: flex; align-items: center; gap: 8px;')
            v-icon(size='18' color='primary') mdi-robot-outline
            span(style='font-weight: 600; flex: 1;') Chorus Assistant
            v-btn(
                v-if='messages.length > 0'
                icon='mdi-restart'
                size='x-small'
                variant='text'
                color='grey-lighten-1'
                title='Start a new conversation'
                @click='resetConversation'
            )
            v-btn(
                icon='mdi-close'
                size='x-small'
                variant='text'
                color='grey-lighten-1'
                title='Close'
                @click='emit("update:modelValue", false)'
            )

        //- Message list
        div(
            ref='scrollContainer'
            style='flex: 1; overflow-y: auto; padding: 12px 10px; display: flex; flex-direction: column; gap: 10px;'
        )
            template(v-if='messages.length === 0 && !isSending')
                div(style='padding: 40px 16px; text-align: center; color: rgba(255,255,255,0.5); font-size: 0.85rem;')
                    v-icon(size='28' color='grey-darken-1') mdi-message-text-outline
                    div(style='margin-top: 12px;') Ask about your assets, contacts, or plans. Try:
                    div(style='margin-top: 10px; display: flex; flex-direction: column; gap: 6px; font-size: 0.78rem;')
                        a(
                            v-for='suggestion in suggestions'
                            :key='suggestion'
                            style='color: #93c5fd; cursor: pointer;'
                            @click='sendSuggestion(suggestion)'
                        ) {{ suggestion }}

            template(v-for='(msg, idx) in messages' :key='idx')
                //- Tool-call cards (collapsed by default; click to expand)
                //- While a tool is in flight (elapsedMs === undefined) show a
                //- spinner + "running…" so the operator sees progress instead
                //- of an unbounded "Thinking…" typing dot.
                template(v-if='msg.role === "tool-trace"')
                    div(style='align-self: stretch; background: rgba(59, 130, 246, 0.08); border: 1px solid rgba(59,130,246,0.25); border-radius: 6px; padding: 6px 10px; font-size: 0.74rem;')
                        div(
                            style='display: flex; align-items: center; gap: 8px; cursor: pointer;'
                            @click='msg.expanded = !msg.expanded'
                        )
                            v-progress-circular(
                                v-if='msg.elapsedMs === undefined'
                                indeterminate size='14' width='2' color='primary'
                            )
                            v-icon(v-else size='14' :color='msg.isError ? "error" : "info"') {{ msg.isError ? "mdi-alert-circle" : "mdi-wrench-outline" }}
                            span(style='font-family: monospace; flex: 1;') {{ msg.name }}({{ Object.keys(msg.input).join(", ") }})
                            span(v-if='msg.elapsedMs === undefined' style='color: rgba(255,255,255,0.5); font-size: 0.7rem;') running…
                            span(v-else style='color: rgba(255,255,255,0.45); font-size: 0.7rem;') {{ msg.elapsedMs }}ms
                            v-icon(v-if='msg.elapsedMs !== undefined' size='14' color='grey-lighten-1') {{ msg.expanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
                        div(v-if='msg.expanded && msg.elapsedMs !== undefined' style='margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255,255,255,0.06);')
                            pre(style='margin: 0; padding: 0; font-size: 0.7rem; max-height: 240px; overflow: auto; color: rgba(255,255,255,0.75); white-space: pre-wrap; word-break: break-all;') {{ formatResult(msg.resultJson) }}

                //- Chat bubbles
                template(v-else)
                    div(:style='bubbleWrapStyle(msg.role)')
                        div(:style='bubbleStyle(msg.role)' v-html='renderMarkdown(msg.text)')

            //- Typing indicator
            div(v-if='isSending' style='align-self: flex-start; display: flex; align-items: center; gap: 8px; padding: 4px 12px; color: rgba(255,255,255,0.6); font-size: 0.78rem;')
                v-progress-circular(indeterminate size='14' width='2' color='primary')
                span Thinking…

        //- Input
        div(style='padding: 10px 12px; border-top: 1px solid rgba(255,255,255,0.08);')
            div(style='display: flex; gap: 6px; align-items: flex-end;')
                v-textarea(
                    v-model='draft'
                    placeholder='Ask anything about your operation…'
                    rows='1'
                    max-rows='5'
                    auto-grow
                    density='compact'
                    variant='outlined'
                    hide-details
                    :disabled='isSending'
                    style='font-size: 0.85rem;'
                    @keydown.enter.exact.prevent='send'
                )
                v-btn(
                    icon='mdi-send'
                    size='small'
                    color='primary'
                    variant='flat'
                    :disabled='!canSend'
                    @click='send'
                )
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue';
import { useRouter } from 'vue-router';
import { authedFetch } from '@/util/authedFetch';
import { alertException } from '@/util/errors';

const router = useRouter();

type Role = 'user' | 'assistant' | 'tool-trace';

interface ChatMessage {
    role: Role;
    text?: string;
    // Tool-trace-only fields
    name?: string;
    input?: Record<string, unknown>;
    resultJson?: string;
    isError?: boolean;
    elapsedMs?: number;
    expanded?: boolean;
}

const props = defineProps<{ modelValue: boolean }>();
const emit = defineEmits<{ (e: 'update:modelValue', v: boolean): void }>();

const messages = ref<ChatMessage[]>([]);
const draft = ref('');
const isSending = ref(false);
const conversationId = ref<string | null>(null);
const scrollContainer = ref<HTMLElement | null>(null);

const suggestions = [
    'What assets do I have available?',
    'List my draft plans',
    'Are there any tankers within 30 km of Vancouver?',
    'Propose options for optimizing my last plan',
];

const canSend = computed(() => draft.value.trim().length > 0 && !isSending.value);

const sendSuggestion = (text: string) => {
    draft.value = text;
    send();
};

// Progress event shapes streamed from the backend as NDJSON.  The
// server emits tool_use as soon as it dispatches a tool, then
// tool_result when that tool returns, then done when the whole turn
// finishes.  On any error the server emits error and closes the
// stream.  Matching tool_result to its in-flight tool_use is done by
// popping the oldest pending trace with a matching name.
type ChatEvent =
    | { type: 'tool_use';    name: string; input: Record<string, unknown> }
    | { type: 'tool_result'; name: string; elapsedMs: number; isError: boolean; preview: string; bytes: number }
    | { type: 'iterating';   iter: number; messageCount: number }
    | { type: 'retrying';    attempt: number; maxRetries: number; delayMs: number; reason: string }
    | { type: 'navigate';    path: string }
    | { type: 'done';        reply: string; conversationId: string; toolCalls: unknown[] }
    | { type: 'error';       message: string };

const send = async () => {
    const text = draft.value.trim();
    if (!text || isSending.value) return;
    draft.value = '';
    messages.value.push({ role: 'user', text });
    isSending.value = true;
    await scrollToBottom();

    // Track pending tool traces so tool_result can update the matching
    // card in place instead of appending a duplicate.  FIFO by index —
    // the backend dispatches tools sequentially so the first
    // in-flight trace with a matching name is the right one.
    const pendingTraces: number[] = [];

    try {
        const resp = await authedFetch('/api/chat', {
            method: 'POST',
            body: JSON.stringify({ message: text, conversationId: conversationId.value }),
        });
        if (!resp.ok) {
            let userMessage: string;
            try {
                const body = await resp.json() as { error?: string };
                userMessage = body?.error?.trim() || `Chat failed (${resp.status}).`;
            } catch {
                userMessage = `Chat failed (${resp.status}).`;
            }
            throw new Error(userMessage);
        }
        if (!resp.body) throw new Error('Chat response has no body.');

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        let doneMessage = '';
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            const lines = buf.split('\n');
            // Keep the last (possibly partial) line in the buffer.
            buf = lines.pop() ?? '';
            for (const line of lines) {
                if (!line.trim()) continue;
                let ev: ChatEvent;
                try { ev = JSON.parse(line) as ChatEvent; }
                catch { continue; }
                switch (ev.type) {
                    case 'tool_use':
                        messages.value.push({
                            role: 'tool-trace',
                            name: ev.name,
                            input: ev.input,
                            resultJson: '',
                            isError: false,
                            elapsedMs: undefined,   // in-flight marker
                            expanded: false,
                        });
                        pendingTraces.push(messages.value.length - 1);
                        await scrollToBottom();
                        break;
                    case 'tool_result': {
                        // Find the oldest pending trace whose name matches.
                        const idx = pendingTraces.findIndex(i =>
                            messages.value[i]?.name === ev.name
                            && messages.value[i]?.elapsedMs === undefined);
                        if (idx >= 0) {
                            const traceIdx = pendingTraces[idx]!;
                            pendingTraces.splice(idx, 1);
                            const trace = messages.value[traceIdx]!;
                            trace.resultJson = ev.preview
                                + (ev.bytes > ev.preview.length ? `\n… (${ev.bytes - ev.preview.length} more bytes)` : '');
                            trace.isError = ev.isError;
                            trace.elapsedMs = ev.elapsedMs;
                        }
                        break;
                    }
                    case 'iterating':
                        // Silent — the individual tool_use/tool_result
                        // cards already show what the assistant is
                        // doing.  Iteration count kept for the log
                        // if we ever surface a status pill later.
                        break;
                    case 'navigate':
                        // The assistant asked the browser to open a
                        // deep link — most commonly the JIPOE tab
                        // hydrated with a freshly-analyzed scenario.
                        // Fire-and-forget: router.push resolves the
                        // link and the operator can immediately see
                        // the result the agent produced.  Errors
                        // (unknown route etc.) are swallowed here;
                        // the assistant's text reply still lands.
                        try { void router.push(ev.path); }
                        catch { /* invalid route — nothing to do */ }
                        break;
                    case 'retrying':
                        // Insert a warning-style trace card so the
                        // operator sees the transient hiccup rather
                        // than assuming the chat is stuck.  Auto-
                        // resolves when the retry succeeds (the next
                        // tool_use / done event lands normally).
                        messages.value.push({
                            role: 'tool-trace',
                            name: `retry (${ev.reason})`,
                            input: { attempt: ev.attempt, maxRetries: ev.maxRetries, delayMs: ev.delayMs },
                            resultJson: `Transient Anthropic ${ev.reason} on attempt ${ev.attempt}. `
                                + `Waiting ${ev.delayMs}ms before retry ${ev.attempt}/${ev.maxRetries}.`,
                            isError: false,
                            elapsedMs: ev.delayMs,
                            expanded: false,
                        });
                        await scrollToBottom();
                        break;
                    case 'done':
                        conversationId.value = ev.conversationId;
                        doneMessage = ev.reply || '(no reply)';
                        break;
                    case 'error':
                        throw new Error(ev.message);
                }
            }
        }
        if (doneMessage) {
            messages.value.push({ role: 'assistant', text: doneMessage });
        }
    } catch (err) {
        messages.value.push({
            role: 'assistant',
            text: `Sorry — request failed. (${err instanceof Error ? err.message : String(err)})`,
        });
        alertException(err, 'Chat request failed');
    } finally {
        isSending.value = false;
        await scrollToBottom();
    }
};

const resetConversation = async () => {
    // Tell the server to drop the history too, otherwise the next message
    // would still arrive with the old context attached.
    if (conversationId.value) {
        try {
            await authedFetch('/api/chat/reset', {
                method: 'POST',
                body: JSON.stringify({ conversationId: conversationId.value }),
            });
        } catch {
            // Non-fatal — worst case the server still holds an orphaned conversation.
        }
    }
    messages.value = [];
    draft.value = '';
    conversationId.value = null;
};

const scrollToBottom = async () => {
    await nextTick();
    const el = scrollContainer.value;
    if (el) el.scrollTop = el.scrollHeight;
};

// Bubble styling — user bubbles right-aligned (primary tint), assistant bubbles
// left-aligned (neutral). Common bubble shape, distinguished by side + color.
const bubbleWrapStyle = (role: Role) => ({
    display: 'flex',
    justifyContent: role === 'user' ? 'flex-end' : 'flex-start',
});
const bubbleStyle = (role: Role) => ({
    maxWidth: '85%',
    padding: '8px 12px',
    borderRadius: '12px',
    background: role === 'user' ? '#1d4ed8' : 'rgba(255,255,255,0.06)',
    color: 'white',
    fontSize: '0.85rem',
    lineHeight: '1.4',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
});

// Minimal markdown renderer — bold, italic, code, links, line breaks, lists.
// We render the assistant's reply through this; it's small enough not to need
// a full markdown lib for now.
const renderMarkdown = (text: string | undefined): string => {
    if (!text) return '';
    let html = escapeHtml(text);
    // Code spans
    html = html.replace(/`([^`]+)`/g, '<code style="background: rgba(255,255,255,0.1); padding: 1px 4px; border-radius: 3px; font-family: monospace; font-size: 0.82em;">$1</code>');
    // Bold
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    // Italic
    html = html.replace(/(^|[^*])\*([^*]+)\*(?!\*)/g, '$1<em>$2</em>');
    return html;
};

const escapeHtml = (s: string) =>
    s.replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

const formatResult = (json: string): string => {
    try {
        return JSON.stringify(JSON.parse(json), null, 2);
    } catch {
        return json;
    }
};

// Scroll to bottom whenever the panel opens.
watch(() => props.modelValue, (open) => {
    if (open) scrollToBottom();
});
</script>
